<template lang="pug">
el-drawer(
  title="個案記錄異動紀錄"
  :visible.sync="drawer"
  direction="rtl"
  size="640px"
  :before-close="handleClose"
  :append-to-body="appendToBody")
  .drawer.flex.column.gap-16
    .desc.text-secondary(v-if="subtitle") {{ subtitle }}
    .empty.text-secondary(v-if="loaded && logs.length === 0") 尚無異動紀錄
    .log.flex.column.gap-10(v-for="log in logs", :key="log.id")
      .header.flex.align-center.gap-6.wrap
        .time {{ $timestampToDateTimeMinute(log.created_at) }}
        el-tag(size="mini", :type="actionTagType(log)") {{ actionText(log.action) }}
        el-tag(v-if="log.was_published", size="mini", type="warning") 發佈後修改
        .operator.text-secondary
          span {{ log.operator.name || '--' }}
          span(v-if="log.operator.impersonated_by")
            | （管理員 {{ log.operator.impersonated_by.name || log.operator.impersonated_by.id }} 代操作）
      .changes.flex.column.gap-8(v-if="log.changes.length > 0")
        .change.flex.column.gap-4(v-for="change in log.changes", :key="change.key")
          .name.text-secondary {{ change.title }}
          template(v-if="change.type === 'list'")
            .flex.align-center.gap-6.wrap
              el-tag(
                v-for="item in change.removed", :key="`r-${item}`",
                size="mini", type="danger") − {{ item }}
              el-tag(
                v-for="item in change.added", :key="`a-${item}`",
                size="mini", type="success") + {{ item }}
          template(v-else)
            .diff.flex.column.gap-4
              .value.before {{ change.before || '（空白）' }}
              .value.after {{ change.after || '（空白）' }}
      .no-change.text-secondary(v-else) 內容無變更
      el-collapse
        el-collapse-item(title="完整內容（異動前／異動後）")
          .full.flex.gap-10
            .side.flex-1.flex.column.gap-6(
              v-for="side in ['before', 'after']", :key="side")
              .side-title {{ side === 'before' ? '異動前' : '異動後' }}
              template(v-if="log[side] && log[side].record")
                .field(v-for="f in fields", :key="f.key")
                  .name.text-secondary {{ f.title }}
                  .value.plain(v-if="f.type === 'list'") {{ listText(log[side].record, f.key).join('、') || '--' }}
                  .value.plain(v-else) {{ log[side].record[f.key] || '--' }}
              .text-secondary(v-else) 尚無個案記錄
              .field
                .name.text-secondary 附件
                .value.plain {{ attachmentNames(log[side]).join('、') || '--' }}
</template>

<script>
import { getAppointmentRecordLogs } from '@/api/reserve';

const ACTIONS = {
  save_draft: '存草稿',
  publish: '發佈',
  republish: '重新發佈',
  attachment_add: '新增附件',
  attachment_delete: '刪除附件',
};

const FIELDS = [
  { key: 'published', title: '狀態', type: 'published' },
  { key: 'medical_history', title: '疾病史、評估或衡鑑結果與建議' },
  { key: 'main_requirement', title: '當事人主訴' },
  { key: 'main_requirement_tags', title: '當事人主訴標籤', type: 'list' },
  { key: 'evaluation', title: '處遇重點摘要/評估' },
  { key: 'evaluation_tags', title: '處遇重點摘要/評估標籤', type: 'list' },
  { key: 'future_plan', title: '處置規劃' },
  { key: 'future_plan_tags', title: '處置規劃標籤', type: 'list' },
  { key: 'note', title: '備注/特殊事件' },
];

export default {
  name: 'record-log-viewer',
  props: {
    appendToBody: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      drawer: false,
      loaded: false,
      subtitle: '',
      logs: [],
      fields: FIELDS.filter((f) => f.type !== 'published'),
    };
  },
  methods: {
    actionText(action) {
      return ACTIONS[action] || action;
    },
    actionTagType(log) {
      if (log.action === 'publish' || log.action === 'republish') {
        return '';
      }
      return 'info';
    },
    tagText(key, tag) {
      // 後端以 gettext 翻譯，沒有翻譯時與原始名稱相同，改查前端的標籤文案
      if (tag.text && tag.text !== tag.tag) {
        return tag.text;
      }
      const block = key.replace(/_tags$/, '');
      return this.$tryMsg(`tag.${block}.${tag.tag}`) || tag.tag;
    },
    listText(record, key) {
      return ((record && record[key]) || []).map((t) => this.tagText(key, t));
    },
    attachmentNames(snapshot) {
      return ((snapshot && snapshot.attachments) || []).map((a) => a.filename);
    },
    publishedText(record) {
      if (!record) {
        return '';
      }
      return record.published ? '已發佈' : '草稿';
    },
    buildChanges(log) {
      const before = log.before?.record || null;
      const after = log.after?.record || null;
      const changes = [];

      FIELDS.forEach((f) => {
        if (f.type === 'list') {
          const b = this.listText(before, f.key);
          const a = this.listText(after, f.key);
          const removed = b.filter((x) => a.indexOf(x) < 0);
          const added = a.filter((x) => b.indexOf(x) < 0);
          if (removed.length > 0 || added.length > 0) {
            changes.push({
              key: f.key, title: f.title, type: 'list', removed, added,
            });
          }
          return;
        }
        const b = f.type === 'published' ? this.publishedText(before) : (before?.[f.key] || '');
        const a = f.type === 'published' ? this.publishedText(after) : (after?.[f.key] || '');
        if (b !== a) {
          changes.push({
            key: f.key, title: f.title, before: b, after: a,
          });
        }
      });

      const b = this.attachmentNames(log.before);
      const a = this.attachmentNames(log.after);
      const removed = b.filter((x) => a.indexOf(x) < 0);
      const added = a.filter((x) => b.indexOf(x) < 0);
      if (removed.length > 0 || added.length > 0) {
        changes.push({
          key: 'attachments', title: '附件', type: 'list', removed, added,
        });
      }

      return changes;
    },
    handleClose(done) {
      done();
    },
    async show(payload) {
      this.drawer = true;
      this.loaded = false;
      this.logs = [];
      this.subtitle = payload?.subtitle || '';

      await this.$execWithLoading(async () => {
        const rsp = await getAppointmentRecordLogs(payload.id);
        this.logs = (rsp.logs || []).map((log) => ({
          ...log,
          changes: this.buildChanges(log),
        }));
        this.loaded = true;
      }, (e) => {
        this.$showAxiosException('讀取異動紀錄失敗', e);
      });
    },
  },
  mounted() {
    this.$on('show', (payload) => {
      this.show(payload);
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.drawer {
  padding: 20px;
  padding-top: 0;
}
.log {
  padding: 12px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
}
.wrap {
  flex-wrap: wrap;
}
.time {
  font-weight: bold;
}
.value {
  white-space: pre-wrap;
  word-break: break-word;
  padding: 4px 8px;
  border-radius: 4px;
  &.before {
    background: $danger-10-color;
    text-decoration: line-through;
  }
  &.after {
    background: #F0F9EB;
  }
  &.plain {
    padding: 0;
  }
}
.full {
  align-items: flex-start;
}
.side-title {
  font-weight: bold;
}
</style>
